namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionTimePeriodOptions {
    // One of the period codes below, or "sinceDates".
    selectedPeriod?: string;
    // Start dates for the "Since Dates" option (end date is the As Of Date parameter).
    sinceDayIds?: number[];
  }

  interface PeriodChoice { code: string; text: string; }

  // Period options shown in the Edit Time Periods modal (AR-010), in mockup order.
  const PERIOD_ROW_1: PeriodChoice[] = [
    { code: "mtd", text: "MTD" },
    { code: "qtd", text: "QTD" },
    { code: "ytd", text: "YTD" },
    { code: "fytd", text: "FYTD" }
  ];
  const PERIOD_ROW_2: PeriodChoice[] = [
    { code: "oneYear", text: "1Y" },
    { code: "threeYears", text: "3Y" },
    { code: "fiveYears", text: "5Y" },
    { code: "sevenYears", text: "7Y" },
    { code: "tenYears", text: "10Y" }
  ];
  const SINCE_INCEPTION: PeriodChoice = { code: "sinceInception", text: "Since Inception" };
  const SINCE_DATES_CODE = "sinceDates";

  // Default period when the modal is opened with nothing selected yet.
  export const DEFAULT_PERIOD_CODE = PERIOD_ROW_1[0].code;

  const SinceDatesHelp = "Create intervals that range from each specified date until the As Of Date.";

  // A "Since Dates" start date must fall within the supported date range and cannot be in the future.
  const SINCE_DATE_MIN = InvData.Util.DateIndexHelper.IndexZeroDate;
  const SINCE_DATE_INVALID_MESSAGE = "Invalid since date. Please enter a date within the available data range.";

  // Widens the "Since Dates" picker's own min/max so it never silently swallows an out-of-range
  // typed date before we can see it (the kendo widget rejects typed dates outside its configured
  // min/max instead of surfacing them) — the actual floor/future check happens in "validate" below.
  // Note: Date years 0-99 get shifted to 1900-1999 by the Date constructor, so 1000 (not 1) avoids that trap.
  const WIDE_OPEN_MIN = new Date(1000, 0, 1);
  const WIDE_OPEN_MAX = new Date(9999, 0, 1);

  /**
   * A "Since Dates" start date is valid when it falls on or after SINCE_DATE_MIN and is not in
   * the future. A null/undefined date (nothing entered yet) is treated as valid -- there's
   * nothing to flag. `now` is injectable so this stays deterministic in tests.
   */
  export function isSinceDateValid(date: Date | null, now: Date = new Date()): boolean {
    if (!date) { return true; }
    return date >= SINCE_DATE_MIN && date <= now;
  }

  /**
   * "Time Period" parameters-bar control for the Attribution Report. Renders a Configure link
   * that opens the Edit Time Periods modal (AR-010). Exactly one period can be selected at a time.
   */
  export class AttributionTimePeriodsParameterControl extends Solovis.UI.BaseControl {
    private _options: AttributionTimePeriodOptions;

    constructor(options) {
      super(options);
      this._options = (typeof options.initialValue !== "undefined" && options.initialValue !== null)
        ? options.initialValue
        : {};
    }

    public doRender() {
      const self = this;

      const configureLink = $("<a>").text("Configure").appendTo(this.container);
      configureLink.click(function () { self._showEditor(); });

      const editLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () { self._showEditor(); });
    }

    private _showEditor() {
      const editorDialog = new EditTimePeriodsDialog({
        options: this._options,
        saveCallback: (data: AttributionTimePeriodOptions) => this.setParameterValue(data)
      });
      editorDialog.show();
    }

    public _setCurrentOptions(data) {
      this._options = data ?? {};
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, AttributionTimePeriodsParameterControl, this._setCurrentOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class EditTimePeriodsDialog {
    private _opts: AttributionTimePeriodOptions;
    private _saveCallback: (data: AttributionTimePeriodOptions) => void;
    private _formBuilder: Solovis.UI.InputControls.FormBuilder;

    public constructor(options: { options: AttributionTimePeriodOptions; saveCallback: (data: AttributionTimePeriodOptions) => void; }) {
      this._opts = options.options ? Solovis.deepCopy(options.options) : {};
      this._saveCallback = options.saveCallback;
    }

    public show() {
      const opts = this._opts;

      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 600,
        labelColumnWidth: 120
      });
      this._formBuilder = formBuilder;

      formBuilder.addEntry({
        name: "timePeriod",
        label: "",
        showValueOnFullRow: true,
        hideLabelElement: true,

        createEditor: (createEditorOptions) => {
          const container = createEditorOptions.container;
          container.css({ "padding": "8px 0 8px 80px", "display": "flex", "flex-direction": "column", "gap": "18px", "align-items": "flex-start" });

          // Fixed-width cells so each column lines up vertically across rows (MTD above 1Y, etc.).
          const CELL_WIDTH = "72px";

          // A single native radio group gives free single-selection (no manual enforcement needed)
          // and fits the hand-built, fixed-width 3-row layout better than a dxRadioGroup widget would.
          const RADIO_GROUP_NAME = _.uniqueId("attributionTimePeriod_");

          const radios: { code: string; element: JQuery; }[] = [];
          let sinceDatesListBuilder: Solovis.UI.VerticalOrderedListBuilder = null;

          const isInitiallySelected = (code: string) =>
            opts.selectedPeriod ? opts.selectedPeriod === code : code === DEFAULT_PERIOD_CODE;

          const makeRadio = (row: JQuery, choice: PeriodChoice, fixedWidth: boolean = true) => {
            const cell = $("<div>").appendTo(row);
            if (fixedWidth) { cell.css({ "width": CELL_WIDTH }); }
            const label = $("<label>").addClass("radio-inline").appendTo(cell);
            const input = $("<input>")
              .attr({ type: "radio", name: RADIO_GROUP_NAME, value: choice.code })
              .prop("checked", isInitiallySelected(choice.code))
              .appendTo(label);
            label.append(document.createTextNode(choice.text));
            radios.push({ code: choice.code, element: input });
            return input;
          };

          const createFlexRow = () => $("<div>").css({ "display": "flex", "flex-direction": "row" }).appendTo(container);

          const row1 = createFlexRow();
          PERIOD_ROW_1.forEach((c) => makeRadio(row1, c));

          const row2 = createFlexRow();
          PERIOD_ROW_2.forEach((c) => makeRadio(row2, c));

          const row3 = createFlexRow();
          makeRadio(row3, SINCE_INCEPTION, false);

          // Since Dates: its own radio, sharing the same group, plus a help icon and the Add Date
          // button, all on one header row that never moves regardless of how many dates are added.
          // The growing list of dates renders in its own row below, indented under the header.
          const sinceBlock = $("<div>").css({ "display": "flex", "flex-direction": "column", "gap": "4px" }).appendTo(container);

          const sinceHeaderRow = $("<div>").css({ "display": "flex", "flex-direction": "row", "align-items": "center", "gap": "8px" }).appendTo(sinceBlock);
          const sinceDatesRadio = makeRadio(sinceHeaderRow, { code: SINCE_DATES_CODE, text: "Since Dates" }, false);
          const helpLink = $("<a>").addClass("fa fa-question-circle").css("cursor", "pointer").appendTo(sinceHeaderRow);
          helpLink.click(() => {
            Solovis.Site.HelpManager.showCustomHelpDialog(SinceDatesHelp, {
              title: "Since Dates",
              portalServices: createEditorOptions.formBuilder ? createEditorOptions.formBuilder.portalServices() : null
            });
          });
          const addDateButton = $("<button>").text("Add Date").appendTo(sinceHeaderRow);

          const sinceDatesContainer = $("<div>").css({ "margin-left": "24px" }).appendTo(sinceBlock);
          const initialDates = (opts.sinceDayIds && opts.sinceDayIds.length)
            ? _.map(opts.sinceDayIds, (dayId: number) => InvData.DayId.toDate(dayId))
            : null;

          sinceDatesListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
            controlHost: sinceDatesContainer,
            itemTypeLabel: "Date",
            showEditLinks: false,
            showAddButton: false,
            values: initialDates,
            editorCreator: function (editorDiv, initialValue) {
              const inputContainer = $("<div>").appendTo(editorDiv);

              const inputEditor = new Solovis.UI.InputControl({
                container: inputContainer,
                type: "date",
                value: initialValue,
                min: WIDE_OPEN_MIN,
                max: WIDE_OPEN_MAX,
                // No validationModel passed -- this makes InputControl render its own inline error
                // right on this row, instead of sharing one model across rows (which only reflects
                // the most recently validated row and can clear a still-invalid row's message).
                validate: function () {
                  const date = this.value();
                  if (!date || isSinceDateValid(date)) {
                    return { isValid: true, message: null };
                  }
                  return { isValid: false, message: SINCE_DATE_INVALID_MESSAGE };
                }
              });

              return {
                getValue: function () {
                  const date = inputEditor.value();
                  return date ? InvData.DayId.forDate(date) : null;
                },
                isValid: () => inputEditor.isValid()
              };
            },
            newValueGenerator: function () { return null; }
          });
          sinceDatesListBuilder.setAsCurrentControl();

          addDateButton.on("click", () => { sinceDatesListBuilder.addValue(null); });

          // The Add Date button only makes sense while Since Dates is the selected period.
          const updateAddButtonEnabled = () => {
            addDateButton.prop("disabled", !sinceDatesRadio.is(":checked"));
          };
          radios.forEach((r) => r.element.on("change", updateAddButtonEnabled));
          updateAddButtonEnabled();

          const editor = {
            getValue: (): AttributionTimePeriodOptions => {
              const result: AttributionTimePeriodOptions = {};
              const checkedRadio = _.find(radios, (r) => r.element.is(":checked"));
              if (checkedRadio) {
                result.selectedPeriod = checkedRadio.code;
                if (checkedRadio.code === SINCE_DATES_CODE) {
                  const dayIds = ((sinceDatesListBuilder.getValues() as number[]) ?? []).filter((d) => d != null);
                  if (dayIds.length) {
                    result.sinceDayIds = dayIds;
                  }
                }
              }
              return result;
            },
            // Invalid/leftover date rows should never block OK when a different period is selected.
            // When Since Dates is selected, at least one date is required -- this is a UX rule so a
            // user cannot save an empty selection. It is not what keeps the table and KPI tiles in
            // step: resolveTimePeriod defaults a blank Time Period (including a legacy saved Since
            // Dates with no dates) to DEFAULT_PERIOD_CODE before either resolver runs.
            isValid: (): boolean => {
              if (!sinceDatesRadio.is(":checked")) {
                return true;
              }
              const dayIds = ((sinceDatesListBuilder.getValues() as number[]) ?? []).filter((d) => d != null);
              return dayIds.length > 0 && sinceDatesListBuilder.getEditors()
                .every((editorInfo) => editorInfo.isValid());
            }
          };

          return editor;
        }
      });

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Time Periods",
        desiredWidth: 640,
        desiredHeight: 360,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          if (this._formBuilder.hasErrors()) {
            return false;
          }
          const newOptions = this._formBuilder.getValueByName("timePeriod") as AttributionTimePeriodOptions;
          this._saveCallback(newOptions ?? {});
          return true;
        }
      });
    }
  }
}
